import {Box, Flex, Divider, Spacer, Heading} from '@chakra-ui/react';
import BoxCard from '../../components/General/BoxCard';
import LinkButton from '../../components/General/LinkButton';
import {CustomCheckbox, CustomRadioGroup, CustomSelect, SubmitButton} from '../../components/General/FormInputs';
import React, {useState, useEffect, useRef} from 'react';
import FormCodes from '../../constants/FormCodes';
import CriteriaCard from '../../components/Reports/CriteriaCard';
import ErrorHelper from '../../service/ErrorHelper';
import LoadingWheel from '../../components/General/LoadingWheel';
import {Question, Report} from '../../service/Urls';
import {get, getReport} from '../../service/ApiService';
import {useForm} from 'react-hook-form';
import {CustomInput, DatePicker} from '../../components/General/FormInputs';
import Visibility from '../../constants/Visibility';
import {LocationSelect} from '../../components/Location/LocationSelect';
import {ArrangerSelect} from '../../components/Arranger/ArrangerSelect';
import {GroupSelect} from '../../components/Group/GroupSelect';
import {ClientSelect} from '../../components/Client/ClientSelect';
import FlexCard from '../../components/General/FlexCard';
import {useTitle} from '../../components/General/Layout';
import {Center} from '@chakra-ui/react';

const AnalysisReports = () => {
  // use setTitle to update the title of the page, otherwise set in the router typically
  const [title, setTitle] = useTitle('');

  // current selected report
  const [reportVal, setReportVal] = useState('');

  // current selected radio button id, needed for radio control
  const [promptId, setPromptId] = useState('');

  // list of report data
  const [sectionList, setSectionsList] = useState('');

  // current sectionId
  const [sectionId, setSectionId] = useState('');
  const [isInitialLoading, setInitialLoading] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [toast, setToast] = useState({});
  const [question, setQuestions] = useState([]);
  const [pdfUrl, setPdfUrl] = useState('');
  const linkRef = useRef(null);

  // for the report options
  const radios = {
    needs: [
      {key: 'At Need', value: 'atNeed'},
      {key: 'PreNeed', value: 'preNeed'},
      {key: 'Both', value: 'both'},
    ],
    comments: [
      {key: 'Comments', value: '0'},
      {key: 'No Comments', value: '1'},
      {key: 'All', value: '2'},
    ],
    valueSatisfaction: [
      {key: 'Value', value: '0'},
      {key: 'Satisfaction', value: '1'},
      {key: 'Both', value: '2'},
    ],
  };

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    loadData();
  }, []);

  // once the pdf url is loaded click the hidden link to open a new tab
  useEffect(() => {
    if (pdfUrl) linkRef.current.click();
  }, [pdfUrl]);

  useEffect(() => {
    if (Array.isArray(sectionList)) {
      sectionList.forEach(section => {
        const obj = section.reports.find(obj => {
          return obj.promptId === promptId;
        });

        if (typeof obj !== 'undefined') {
          setReportVal(obj);
        }
      });
    }
  }, [promptId]);

  useEffect(() => {
    defaultDates();
  }, [reportVal]);

  const defaultDates = () => {
    const formValues = getValues();
    const Today = new Date().toISOString().split('T')[0];
    if (reportVal.hasOwnProperty('startDateState')) setValue('startDate', formValues.startDate || Today);
    if (reportVal.hasOwnProperty('startDate2State')) setValue('startDate2', formValues.startDate2 || Today);
    if (reportVal.hasOwnProperty('endDateState')) setValue('endDate', formValues.endDate || Today);
    if (reportVal.hasOwnProperty('endDate2State')) setValue('endDate2', formValues.endDate2 || Today);
  };

  const filterFields = data => {
    if (!reportVal.hasOwnProperty('secondaryArrangerState')) data.secondaryArranger = false;
    if (!reportVal.hasOwnProperty('startDateState')) data.startDate = '';
    if (!reportVal.hasOwnProperty('startDate2State')) data.startDate2 = '';
    if (!reportVal.hasOwnProperty('endDateState')) data.endDate = '';
    if (!reportVal.hasOwnProperty('endDate2State')) data.endDate2 = '';
    if (!reportVal.hasOwnProperty('atPreNeedState')) data.atPreNeed = null;
    if (!reportVal.hasOwnProperty('commentsState')) data.comments = '';
    if (!reportVal.hasOwnProperty('minSurveysState')) data.minSurveys = '';
    if (!reportVal.hasOwnProperty('arrangerState')) data.isExcludeFromSum = false;
    if (!reportVal.hasOwnProperty('tpiState')) data.tpiState = false;
    if (!reportVal.hasOwnProperty('questionState')) data.question = null;
    if (!reportVal.hasOwnProperty('commentByLocationState')) data.commentByLocation = false;
    if (!reportVal.hasOwnProperty('scanPhraseState')) data.scanForPhrase = '';
  };

  const loadData = async () => {
    const data = await get(Report.LIST + `/` + FormCodes.ANALYSIS_FUNERAL);
    setToast(data);
    if (!data.error) {
      const sections = data.data;
      if (sections) {
        setPromptId(sections[0].reports[0].promptId);
        setSectionsList(sections);
        setSectionId(sections[0].sectionId);
        setReportVal(sections[0]?.reports[0]);
      }
    }
    const questions = await get(`/Question/get-by-csv?Csvctype=U`);
    setToast(questions);
    if (!questions.error) {
      setQuestions(questions.data);
    }
    setInitialLoading(false);
    setLoading(false);
    setTitle('Analysis Reports - Funeral');
  };

  const ReportCards = () => {
    const col1 = [];
    const col2 = [];
    for (let i = 0; i < sectionList.length; i++) {
      const sec = sectionList[i];
      if (i < 2) {
        col1.push(
          <CriteriaCard value={promptId} setValue={setPromptId} reportInfo={sec} key={sec.sectionId} setSectionId={setSectionId}>
            reports
          </CriteriaCard>
        );
      } else {
        col2.push(
          <CriteriaCard value={promptId} setValue={setPromptId} reportInfo={sec} key={sec.sectionId} setSectionId={setSectionId}>
            reports
          </CriteriaCard>
        );
      }
    }
    return (
      <>
        <Box m="3">
          {col1}
          <FlexCard>
            <CustomCheckbox
              name="Secondary Arrangers"
              id="isSecondaryArranger"
              control={control}
              errors={errors}
              required={reportVal?.secondaryArrangerState === Visibility.REQUIRED}
              disabled={!reportVal.hasOwnProperty('secondaryArrangerState')}
            />
          </FlexCard>
          <FlexCard>
            <CustomInput
              id="scanForPhrase"
              register={register}
              errors={errors}
              name="Scan For:"
              required={reportVal?.scanPhraseState === Visibility.REQUIRED}
              disabled={!reportVal.hasOwnProperty('scanPhraseState')}
              w="100%"
            />
          </FlexCard>
        </Box>
        <Box m="3">{col2}</Box>
      </>
    );
  };

  const submit = async data => {
    setLoading(true);
    data.formCode = FormCodes.ANALYSIS_FUNERAL;
    data.sectionId = sectionId;
    data.promptId = reportVal.promptId;
    data.serviceType = 1;
    if (data.importAnalysis) data[data.importAnalysis] = true;
    filterFields(data);
    const reportName = await getReport(data);
    setToast(reportName);
    if (reportName) {
      if (reportName.error) {
        setToast(reportName);
      } else {
        setPdfUrl(reportName);
      }
    }
    setLoading(false);
  };

  return (
    <>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
      {isInitialLoading ? (
        <LoadingWheel />
      ) : (
        <form onSubmit={handleSubmit(submit)}>
          <Flex>
            {/* Sections */}
            <ReportCards />

            {/* Report Options */}
            <Box m="3" textAlign={'start'}>
              <BoxCard>
                <Heading>Report Options</Heading>
                <Divider my="2" />
                <Flex>
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Starting Date"
                    id="startDate"
                    required={reportVal?.startDateState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('startDateState')}
                  />
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Starting Date 2"
                    id="startDate2"
                    required={reportVal?.startDate2State === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('startDate2State')}
                  />
                </Flex>
                <Flex>
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Ending Date"
                    id="endDate"
                    required={reportVal?.endDateState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('endDateState')}
                  />
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Ending Date 2"
                    id="endDate2"
                    required={reportVal?.endDate2State === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('endDate2State')}
                  />
                </Flex>

                <Divider my="2" />
                <Heading size="md" mb="1">
                  Used for CFS Summary Report
                </Heading>
                <CustomRadioGroup
                  list={radios.needs}
                  control={control}
                  errors={errors}
                  id="atPreneedBoth"
                  defaultValue={'0'}
                  required={reportVal?.atPreNeedState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('atPreNeedState')}
                />
                <CustomRadioGroup
                  list={radios.comments}
                  control={control}
                  errors={errors}
                  id="commentsNoCommentsAll"
                  defaultValue={'2'}
                  required={reportVal?.commentsState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('commentsState')}
                />

                <Divider my="2" />
                <ClientSelect
                  register={register}
                  errors={errors}
                  id={'clientCode'}
                  required={reportVal?.clientIdState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('clientIdState')}
                  setRHFValue={setValue}
                  setId
                />

                <Divider my="2" />
                <LocationSelect
                  register={register}
                  errors={errors}
                  id={'locationId'}
                  required={reportVal?.locationState === Visibility.REQUIRED}
                  clientId={watch('clientId')}
                  disabled={!reportVal.hasOwnProperty('locationState')}
                  setRHFValue={setValue}
                />

                <Divider my="2" />
                <CustomCheckbox
                  control={control}
                  id="isExcludeFromSum"
                  name="Exclude From Sum"
                  ml="2"
                  required={reportVal?.arrangerState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('arrangerState')}
                />
                <ArrangerSelect
                  register={register}
                  errors={errors}
                  id={'arrangerId'}
                  required={reportVal?.arrangerState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('arrangerState')}
                  setRHFValue={setValue}
                  clientId={watch('clientId')}
                />

                <Divider my="2" />
                <GroupSelect
                  register={register}
                  errors={errors}
                  id={'groupId'}
                  required={reportVal?.groupState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('groupState')}
                  setRHFValue={setValue}
                />

                <Divider my="2" />
                <CustomRadioGroup
                  list={radios.valueSatisfaction}
                  control={control}
                  errors={errors}
                  id="valueSatisfactionBoth"
                  defaultValue={'0'}
                  required={reportVal?.valueSatisfactionState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('valueSatisfactionState')}
                />
                <Flex>
                  <Box w="70%">
                    <CustomInput
                      id="minNumSurveys"
                      name="Minimum No. of Surveys"
                      register={register}
                      errors={errors}
                      required={reportVal?.minSurveysState === Visibility.REQUIRED}
                      disabled={!reportVal.hasOwnProperty('minSurveysState')}
                    />
                  </Box>
                  <Box>
                    <CustomCheckbox id="isTPI" name="TPI" control={control} disabled={!reportVal.hasOwnProperty('tpiState')} mt="5" />
                  </Box>
                </Flex>

                <Divider my="2" />
                <CustomSelect
                  id="questionSelected"
                  name="Question"
                  register={register}
                  errors={errors}
                  required={reportVal?.questionState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('questionState')}
                >
                  {question.map((q, idx) => {
                    return (
                      <option value={q.qmNumber} key={idx}>
                        {q.qmDesc} | {q.qmNumber}
                      </option>
                    );
                  })}
                </CustomSelect>

                <Divider my="2" />
                <CustomCheckbox
                  id="isCommentReportByLocation"
                  name="Comment Report By Location"
                  control={control}
                  disabled={!reportVal.hasOwnProperty('commentByLocationState')}
                />

                <Divider my="2" />

                {isLoading ? (
                  <Flex justify={'center'}>
                    <LoadingWheel />
                  </Flex>
                ) : (
                  <>
                    <Flex>
                      <Box w="45%">
                        <LinkButton to="/" label="Back" />
                      </Box>
                      <Spacer />
                      <Box w="45%">
                        <SubmitButton name="Print" />
                      </Box>
                    </Flex>
                  </>
                )}
              </BoxCard>
            </Box>
          </Flex>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default AnalysisReports;
